import React, { useEffect, useState } from 'react';
import { BookOpen, Sparkles, FolderOpen, Rss } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { getDigestPreview, DigestPreviewDocket } from '../lib/watchlistDigestService';
import { PersonalizedSummaryDashboard } from '../components/PersonalizedSummaryDashboard';
import { LibraryFeedSection } from '../components/library/LibraryFeedSection';
import { LibraryDocketsTab } from '../components/library/LibraryDocketsTab';

// ── Types ──────────────────────────────────────────────────────────────────

type LibraryTab = 'feed' | 'summaries' | 'dockets';

function parseTab(raw: string | null): LibraryTab {
  if (raw === 'feed' || raw === 'summaries' || raw === 'dockets') return raw;
  // backward-compat: old deep-links used ?tab=summaries / ?tab=dockets directly
  return 'feed';
}

const TABS: { id: LibraryTab; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { id: 'feed',      label: 'Feed',       Icon: Rss },
  { id: 'summaries', label: 'Summaries',  Icon: Sparkles },
  { id: 'dockets',   label: 'My Dockets', Icon: FolderOpen },
];

// ── Component ──────────────────────────────────────────────────────────────

export const SummaryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Tab state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<LibraryTab>(() =>
    parseTab(searchParams.get('tab'))
  );

  // Sync URL → state when the query param changes externally (e.g. <Link> clicks)
  useEffect(() => {
    const next = parseTab(searchParams.get('tab'));
    if (next !== activeTab) setActiveTab(next);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTab = (tab: LibraryTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  // ── Shared digest data ───────────────────────────────────────────────────
  // Fetched lazily on tabs that actually use it (Feed renders new-filing
  // chips; Dockets shows staleness counts). The Summaries tab only uses
  // digestDockets for the optional "X new filings since" hint on each
  // card, which isn't worth gating page load on — it backfills when the
  // user switches tabs.
  const [digestDockets, setDigestDockets] = useState<DigestPreviewDocket[]>([]);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestFetched, setDigestFetched] = useState(false);

  useEffect(() => {
    if (digestFetched) return;
    if (activeTab !== 'feed' && activeTab !== 'dockets') return;
    setDigestLoading(true);
    setDigestFetched(true);
    getDigestPreview()
      .then(preview => setDigestDockets(preview.dockets))
      .catch(() => setDigestDockets([]))   // silent fail – not all users have a watchlist
      .finally(() => setDigestLoading(false));
  }, [activeTab, digestFetched]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto">

      {/* Page header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900">
          <BookOpen className="h-4 w-4 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">My Library</h1>
          <p className="text-sm text-gray-500">
            Your dockets, summaries, and team activity — all in one place
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="mb-6 flex border-b border-gray-200">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => selectTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-all ${
              activeTab === id
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'feed' && (
        <LibraryFeedSection
          digestDockets={digestDockets}
          digestLoading={digestLoading}
        />
      )}

      {activeTab === 'summaries' && (
        <PersonalizedSummaryDashboard
          digestDockets={digestDockets}
        />
      )}

      {activeTab === 'dockets' && (
        <LibraryDocketsTab
          digestDockets={digestDockets}
        />
      )}
    </div>
  );
};
